/**
 * How a failed sign-in or registration is shown to the player. `credentials` covers wrong and unknown credentials
 * alike, so sign-in never reveals whether an account exists. `not-allowed` is a refusal by the pre-launch sign-up
 * allowlist (see notAllowedByAllowlist). `cancelled` is a player who backed out of a social provider's sign-in, which
 * is never an error. `storage` is a browser that won't keep the state a social sign-in redirect needs.
 * `different-method` is a social sign-in with the address of an account created another way: accounts are never linked
 * (decision 4 of ENG-924), so the player must sign in as they did before.
 */
export type AuthFailure =
  | 'credentials'
  | 'account-exists'
  | 'different-method'
  | 'password-policy'
  | 'too-many-requests'
  | 'network'
  | 'not-allowed'
  | 'cancelled'
  | 'storage'
  | 'unknown';

const FAILURES: Readonly<Record<string, AuthFailure>> = {
  'auth/invalid-credential': 'credentials',
  'auth/invalid-email': 'credentials',
  'auth/user-disabled': 'credentials',
  'auth/user-not-found': 'credentials',
  'auth/wrong-password': 'credentials',
  'auth/email-already-in-use': 'account-exists',
  'auth/account-exists-with-different-credential': 'different-method',
  'auth/password-does-not-meet-requirements': 'password-policy',
  'auth/weak-password': 'password-policy',
  'auth/too-many-requests': 'too-many-requests',
  'auth/network-request-failed': 'network',
  'auth/user-cancelled': 'cancelled',
  'auth/redirect-cancelled-by-user': 'cancelled',
  'auth/popup-closed-by-user': 'cancelled',
  'auth/web-storage-unsupported': 'storage',
  'auth/operation-not-supported-in-this-environment': 'storage',
};

/**
 * The message of every refusal by infrastructure/identity-allowlist's blocking functions (REFUSAL_MARKER in its
 * src/gate.ts). Identity Platform passes it through in the error message.
 */
export const ALLOWLIST_REFUSAL_MARKER = 'SIGN_UP_NOT_ALLOWED';

const ALLOWLIST_REFUSAL = new RegExp(`\\b${ALLOWLIST_REFUSAL_MARKER}\\b`);

/**
 * Messages shared by sign-in and registration. Refusals by the allowlist read the same for every address and never
 * hint at which addresses are allowed. `differentMethod` never names the account's method: email enumeration
 * protection is on.
 */
export const AUTH_MESSAGES = {
  tooManyRequests: 'Too many attempts. Wait a few minutes and try again.',
  network: 'Check your connection and try again.',
  registrationNotOpen: "Registration isn't open yet.",
  signInNotOpen: "This account can't sign in yet.",
  storage: 'Your browser is blocking what sign-in needs. Allow cookies and site data for this site, or use email.',
  differentMethod: 'You already have an account with this email address. Sign in the way you did before.',
} as const;

/**
 * The error's Firebase code, e.g. auth/invalid-credential, or undefined. The only detail of an auth error that may be
 * logged: Firebase errors also carry the email address and token responses (customData, _tokenResponse).
 */
export const authErrorCode = (error: unknown): string | undefined => {
  const code = (error as { code?: unknown } | null | undefined)?.code;
  return typeof code === 'string' ? code : undefined;
};

/**
 * Whether a blocking function refused the account because of the allowlist. Such a refusal arrives as
 * auth/internal-error carrying the marker in its message; any other blocking-function failure (a timeout or a crash)
 * is an internal error without it.
 */
export const notAllowedByAllowlist = (error: unknown): boolean => {
  const message = (error as { message?: unknown } | null | undefined)?.message;
  return (
    authErrorCode(error) === 'auth/internal-error' && typeof message === 'string' && ALLOWLIST_REFUSAL.test(message)
  );
};

export const authFailure = (error: unknown): AuthFailure =>
  notAllowedByAllowlist(error) ? 'not-allowed' : (FAILURES[authErrorCode(error) ?? ''] ?? 'unknown');

/** The message for a failure either flow shows the same way, or undefined for one the caller words itself. */
export const sharedAuthMessage = (failure: AuthFailure): string | undefined => {
  switch (failure) {
    case 'too-many-requests':
      return AUTH_MESSAGES.tooManyRequests;
    case 'network':
      return AUTH_MESSAGES.network;
    case 'storage':
      return AUTH_MESSAGES.storage;
    case 'different-method':
      return AUTH_MESSAGES.differentMethod;
    default:
      return undefined;
  }
};
